import { FormEvent, useState } from 'react';

import { verifyCognitoPassword } from '@/auth/utils/cognitoMfa';
import type { CognitoMfaType } from '@/auth/utils/cognitoSignIn';
import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

import { type RunExclusive, mfaErrorMessage } from './mfaErrorMessage';

const MISSING_EMAIL =
  'We could not find the email address on your account. Reload the page and try again.';

// The method being set up, and the methods it replaces, as the message names them.
const SETTING_UP: Record<CognitoMfaType, string> = {
  TOTP: 'an authenticator app',
  SMS: 'text message codes',
  EMAIL: 'email codes',
};
const REPLACED: Record<CognitoMfaType, string> = {
  TOTP: 'authenticator app codes',
  SMS: 'text message codes',
  EMAIL: 'email codes',
};

export function switchMessage(type: CognitoMfaType, turnOff: CognitoMfaType[]): string {
  const others = turnOff.map((other) => REPLACED[other]).join(' and ');
  return `Only a single 2FA method can be active. Setting up ${SETTING_UP[type]} replaces ${others}.`;
}

interface CognitoMfaSwitchFormProps {
  /** The method about to be turned on. */
  type: CognitoMfaType;
  /** The methods on now, which it replaces. */
  turnOff: CognitoMfaType[];
  /** The pool username. */
  email?: string;
  /** Starts turning `type` on once the password is confirmed. */
  onConfirmed: () => void;
  onCancel: () => void;
  /** Holds the settings page's change lock while the password is checked. */
  runExclusive: RunExclusive;
}

/**
 * One two-factor method at a time (ENG-6323): asks before one replaces
 * another, and for the password, since the switch turns a method off and
 * turning one off always asks for it (ENG-6107).
 */
export default function CognitoMfaSwitchForm({
  type,
  turnOff,
  email,
  onConfirmed,
  onCancel,
  runExclusive,
}: CognitoMfaSwitchFormProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // The pool's username is the email; without it the check cannot run.
    if (!email) {
      setError(MISSING_EMAIL);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await runExclusive(() => verifyCognitoPassword(email, password));
    } catch (err) {
      setError(mfaErrorMessage(err));
      setPassword('');
      setBusy(false);
      return;
    }
    onConfirmed();
  }

  return (
    <form className="flex flex-col gap-16" onSubmit={handleSubmit}>
      <p className="text-on-surface">{switchMessage(type, turnOff)}</p>
      <Input
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!error}
        helperText={error ?? 'Re-enter your password to change your two-factor method.'}
        inputProps={{ autoComplete: 'current-password' }}
      />
      <div className="flex justify-end gap-8">
        <Button type="button" variant="tertiary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" disabled={!password || busy}>
          Confirm
        </Button>
      </div>
    </form>
  );
}
